import { useState, useRef, useEffect } from "react";

// ── Constants ────────────────────────────────────────────────────────────────
const ZONES = [
  { id: "tl", label: "Top Left",      x: "18%",  y: "18%" },
  { id: "tc", label: "Top Centre",    x: "50%",  y: "12%" },
  { id: "tr", label: "Top Right",     x: "82%",  y: "18%" },
  { id: "ml", label: "Mid Left",      x: "22%",  y: "50%" },
  { id: "mc", label: "Mid Centre",    x: "50%",  y: "46%" },
  { id: "mr", label: "Mid Right",     x: "78%",  y: "50%" },
  { id: "bl", label: "Bottom Left",   x: "28%",  y: "76%" },
  { id: "bc", label: "Bottom Centre", x: "50%",  y: "78%" },
  { id: "br", label: "Bottom Right",  x: "72%",  y: "76%" },
];

function getRandom(arr) { return arr[Math.floor(Math.random() * arr.length)]; }
function getShotZone() { return getRandom(ZONES).id; }

const REACT_WINDOW_MS = 1000;

const SAVE_COMMENTS = [
  "WHAT A SAVE! Brilliant reading of the shot! 🧤",
  "DENIED! Straight to the right spot! 🧤",
  "Incredible reflexes — palmed away! 🧤",
  "You read it perfectly! 🧤",
  "Stonewall save — the crowd erupts! 🧤",
];
const GOAL_CONCEDED_COMMENTS = [
  "GOAL! Wrong way — back of the net! 😬",
  "GOAL! Too slow to react! 😬",
  "GOAL! Beaten all ends up! 😬",
  "GOAL! No chance that time! 😬",
];
const NO_CHOICE_COMMENTS = [
  "You didn't move — GOAL! ⏱️",
  "Frozen on your line — GOAL! ⏱️",
];

// ── Striker SVG ───────────────────────────────────────────────────────────────
function Striker({ shooting, shotZone }) {
  const kicking = shooting && !!shotZone;
  return (
    <div style={{
      position: "absolute", bottom: "6%", left: "50%",
      transform: "translateX(-50%)",
      width: 70, height: 90, zIndex: 2,
    }}>
      <svg viewBox="-35 -80 70 90" width="70" height="90" style={{ overflow: "visible", filter: "drop-shadow(0 4px 10px #000b)" }}>
        {/* Legs */}
        <rect x="-9" y="-6" width="8" height="26" rx="4" fill="#1f2937" style={{
          transform: kicking ? "rotate(35deg)" : "rotate(0deg)",
          transformOrigin: "-5px -4px",
          transition: "transform 0.15s ease-out"
        }} />
        <rect x="1" y="-6" width="8" height="26" rx="4" fill="#1f2937" />
        {/* Boots */}
        <ellipse cx="-5" cy="22" rx="7" ry="4" fill="#111" />
        <ellipse cx="5" cy="22" rx="7" ry="4" fill="#111" />
        {/* Shirt */}
        <rect x="-14" y="-36" width="28" height="32" rx="6" fill="#ef4444" />
        <rect x="-3" y="-36" width="6" height="32" rx="2" fill="#fff" opacity="0.4" />
        {/* Arms */}
        <rect x="-22" y="-34" width="10" height="9" rx="4" fill="#ef4444" />
        <rect x="12" y="-34" width="10" height="9" rx="4" fill="#ef4444" />
        {/* Neck */}
        <rect x="-5" y="-44" width="10" height="10" rx="3" fill="#f5c5a3" />
        {/* Head */}
        <ellipse cx="0" cy="-52" rx="12" ry="11" fill="#f5c5a3" />
        {/* Hair */}
        <ellipse cx="0" cy="-60" rx="12" ry="6" fill="#1f2937" />
        {/* Ball at feet */}
        {!kicking && <text x="14" y="24" fontSize="14">⚽</text>}
      </svg>
    </div>
  );
}

// ── Keeper SVG (player-controlled) ──────────────────────────────────────────
function PlayerKeeper({ diveZone, diving }) {
  let bodyTransform = "translate(0,0) rotate(0deg)";
  let gloveL = { x: -18, y: -28 };
  let gloveR = { x: 18, y: -28 };

  if (diving && diveZone) {
    const isLeft  = diveZone.endsWith("l");
    const isRight = diveZone.endsWith("r");
    const isTop   = diveZone.startsWith("t");

    if (isLeft) {
      bodyTransform = "translate(-44px, 10px) rotate(-50deg)";
      gloveL = { x: -32, y: -14 };
      gloveR = { x: 8, y: -36 };
    } else if (isRight) {
      bodyTransform = "translate(44px, 10px) rotate(50deg)";
      gloveL = { x: -8, y: -36 };
      gloveR = { x: 32, y: -14 };
    } else if (isTop) {
      bodyTransform = "translate(0, -24px) rotate(0deg)";
      gloveL = { x: -22, y: -46 };
      gloveR = { x: 22, y: -46 };
    } else {
      bodyTransform = "translate(0, 6px) rotate(0deg)";
      gloveL = { x: -22, y: -10 };
      gloveR = { x: 22, y: -10 };
    }
  }

  return (
    <div style={{ position: "absolute", bottom: "20%", left: "50%", transform: "translateX(-50%)", width: 80, height: 100, zIndex: 3 }}>
      <svg viewBox="-40 -80 80 90" width="80" height="90" style={{
        overflow: "visible",
        transform: bodyTransform,
        transition: "transform 0.28s cubic-bezier(.3,1.5,.5,1)",
        filter: "drop-shadow(0 4px 10px #000b)",
      }}>
        <rect x="-10" y="-6" width="9" height="28" rx="4" fill="#1e3a5f" />
        <rect x="1" y="-6" width="9" height="28" rx="4" fill="#1e3a5f" />
        <ellipse cx="-6" cy="24" rx="8" ry="5" fill="#111" />
        <ellipse cx="6" cy="24" rx="8" ry="5" fill="#111" />
        <rect x="-16" y="-38" width="32" height="34" rx="6" fill="#3b82f6" />
        <rect x="-4" y="-38" width="8" height="34" rx="2" fill="#60a5fa" opacity="0.5" />
        <rect x="-26" y="-36" width="12" height="9" rx="4" fill="#3b82f6" />
        <rect x="14" y="-36" width="12" height="9" rx="4" fill="#3b82f6" />
        <ellipse cx={gloveL.x} cy={gloveL.y} rx="9" ry="8" fill="#10b981" style={{ transition: "cx 0.28s cubic-bezier(.3,1.5,.5,1), cy 0.28s cubic-bezier(.3,1.5,.5,1)" }} />
        <ellipse cx={gloveR.x} cy={gloveR.y} rx="9" ry="8" fill="#10b981" style={{ transition: "cx 0.28s cubic-bezier(.3,1.5,.5,1), cy 0.28s cubic-bezier(.3,1.5,.5,1)" }} />
        <rect x="-6" y="-48" width="12" height="12" rx="4" fill="#f5c5a3" />
        <ellipse cx="0" cy="-58" rx="14" ry="13" fill="#f5c5a3" />
        <ellipse cx="0" cy="-68" rx="14" ry="7" fill="#374151" />
        <circle cx="-5" cy="-58" r="2.5" fill="#1f2937" />
        <circle cx="5" cy="-58" r="2.5" fill="#1f2937" />
        <circle cx="-4" cy="-58" r="1" fill="#fff" />
        <circle cx="6" cy="-58" r="1" fill="#fff" />
        <path d="M -4 -52 Q 0 -50 4 -52" stroke="#c2410c" strokeWidth="1.5" fill="none" />
        <text x={gloveL.x} y={gloveL.y+2} textAnchor="middle" fill="#fff" fontSize="4" fontWeight="bold">GK</text>
        <text x={gloveR.x} y={gloveR.y+2} textAnchor="middle" fill="#fff" fontSize="4" fontWeight="bold">GK</text>
      </svg>
    </div>
  );
}

// ── Ball ──────────────────────────────────────────────────────────────────────
function Ball({ shooting, targetZone }) {
  const zone = ZONES.find(z => z.id === targetZone);
  const style = shooting && targetZone ? {
    position: "absolute",
    left: zone?.x || "50%",
    top: zone?.y || "50%",
    transform: "translate(-50%, -50%) scale(0.7)",
    transition: `all ${REACT_WINDOW_MS}ms cubic-bezier(.2,1,.6,1)`,
    fontSize: 26, zIndex: 4,
    filter: "drop-shadow(0 4px 12px #000c)",
  } : {
    position: "absolute", bottom: "9%", left: "50%",
    transform: "translateX(-50%)",
    fontSize: 26, zIndex: 4,
    filter: "drop-shadow(0 2px 8px #000a)",
  };
  return <div style={style}>⚽</div>;
}

// ── Goal ──────────────────────────────────────────────────────────────────────
function Goal() {
  return (
    <div style={{
      position: "absolute", top: "6%", left: "50%", transform: "translateX(-50%)",
      width: "74%", height: "54%", border: "4px solid #fff", borderBottom: "none",
      borderRadius: "4px 4px 0 0", boxShadow: "0 0 0 2px #0006, inset 0 0 40px #ffffff06",
      zIndex: 1, background: "linear-gradient(180deg,#ffffff06 0%,transparent 100%)",
    }}>
      {[25,50,75].map(p => <div key={p} style={{ position:"absolute", top:`${p}%`, left:0, right:0, height:1, background:"#ffffff18" }} />)}
      {[20,40,60,80].map(p => <div key={p} style={{ position:"absolute", left:`${p}%`, top:0, bottom:0, width:1, background:"#ffffff18" }} />)}
    </div>
  );
}

// ── Zone Buttons ──────────────────────────────────────────────────────────────
function ZoneButtons({ onDive, disabled, selected }) {
  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, width:"100%", maxWidth:340 }}>
      {ZONES.map(zone => {
        const isSelected = selected === zone.id;
        return (
          <button key={zone.id} onClick={() => onDive(zone.id)} disabled={disabled}
            style={{
              background: isSelected ? "#3b82f6" : disabled ? "#1f2937" : "#ffffff10",
              border: isSelected ? "1px solid #3b82f6" : "1px solid #ffffff20", borderRadius: 10,
              color: isSelected ? "#fff" : disabled ? "#4b5563" : "#f9fafb",
              padding: "10px 4px", fontSize: 11, fontWeight: 700,
              fontFamily: "'Oswald', sans-serif", letterSpacing: 0.5,
              cursor: disabled ? "not-allowed" : "pointer", transition: "all 0.15s",
            }}
            onMouseEnter={e => { if (!disabled && !isSelected) { e.currentTarget.style.background="#3b82f633"; e.currentTarget.style.borderColor="#3b82f6"; }}}
            onMouseLeave={e => { if (!isSelected) { e.currentTarget.style.background=disabled?"#1f2937":"#ffffff10"; e.currentTarget.style.borderColor="#ffffff20"; }}}
          >{zone.label}{isSelected ? " ✓" : ""}</button>
        );
      })}
    </div>
  );
}

// ── Score Dots ────────────────────────────────────────────────────────────────
function ScoreRow({ saved, total, taken }) {
  return (
    <div style={{ display:"flex", gap:8, justifyContent:"center" }}>
      {Array.from({ length: total }).map((_,i) => {
        const t = i < taken;
        const s = i < saved;
        return (
          <div key={i} style={{
            width:34, height:34, borderRadius:"50%",
            border:`2px solid ${t?(s?"#10b981":"#ef4444"):"#374151"}`,
            background: t?(s?"#10b98133":"#ef444422"):"transparent",
            display:"flex", alignItems:"center", justifyContent:"center",
            fontSize:16, transition:"all 0.3s",
          }}>{t?(s?"🧤":"⚽"):""}</div>
        );
      })}
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────
export default function SaveThePenalty({ onBack }) {
  const TOTAL = 5;
  const [phase, setPhase]       = useState("intro");
  const [saved, setSaved]       = useState(0);
  const [taken, setTaken]       = useState(0);
  const [shooting, setShooting] = useState(false);
  const [shotZone, setShotZone] = useState(null);
  const [diveZone, setDiveZone] = useState(null);
  const [diving, setDiving]     = useState(false);
  const [result, setResult]     = useState(null);
  const [comment, setComment]   = useState("");
  const [history, setHistory]   = useState([]);
  const [canChoose, setCanChoose] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [selectedZone, setSelectedZone] = useState(null);
  const timer = useRef(null);
  const countdownTimer = useRef(null);
  const chosenZoneRef = useRef(null);
  const pendingShotZoneRef = useRef(null);

  useEffect(() => () => {
    clearTimeout(timer.current);
    clearInterval(countdownTimer.current);
  }, []);

  const startShot = () => {
    if (shooting || taken >= TOTAL) return;
    const zone = getShotZone();
    pendingShotZoneRef.current = zone;
    chosenZoneRef.current = null;

    setShotZone(null);
    setShooting(true);
    setCanChoose(true);   // can pick a direction right away
    setDiving(false);
    setDiveZone(null);
    setSelectedZone(null);
    setCountdown(2);

    // Countdown ticks down for the player to see
    countdownTimer.current = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) {
          clearInterval(countdownTimer.current);
          return 0;
        }
        return c - 1;
      });
    }, 1000);

    // After 2 seconds, the shot fires using whatever was chosen
    timer.current = setTimeout(() => {
      clearInterval(countdownTimer.current);
      setCanChoose(false);
      const chosen = chosenZoneRef.current;
      setShotZone(zone);
      if (chosen) {
        setDiving(true);
        setDiveZone(chosen);
      }
      finishShot(chosen, zone);
    }, 2000);
  };

  const choose = (zone) => {
    if (!canChoose) return;
    chosenZoneRef.current = zone;
    setSelectedZone(zone);
  };

  const finishShot = (chosenZone, actualZone) => {
    let outcome, comment;
    if (chosenZone === null) {
      outcome = "noChoice";
      comment = getRandom(NO_CHOICE_COMMENTS);
    } else if (chosenZone === actualZone) {
      outcome = "saved";
      comment = getRandom(SAVE_COMMENTS);
    } else {
      outcome = "conceded";
      comment = getRandom(GOAL_CONCEDED_COMMENTS);
    }


    timer.current = setTimeout(() => {
      setResult(outcome);
      const ns = outcome === "saved" ? saved + 1 : saved;
      const nt = taken + 1;
      setSaved(ns);
      setTaken(nt);
      setHistory(h => [...h, outcome]);
      setComment(comment);

      timer.current = setTimeout(() => {
        setShooting(false);
        setShotZone(null);
        setDiveZone(null);
        setDiving(false);
        setResult(null);
        setComment("");
        if (nt >= TOTAL) setTimeout(() => setPhase("result"), 300);
      }, 1700);
    }, 350);
  };

  const restart = () => {
    setSaved(0); setTaken(0); setShooting(false); setShotZone(null);
    setDiveZone(null); setDiving(false); setResult(null); setComment("");
    setHistory([]); setCanChoose(false); setCountdown(0); setSelectedZone(null); setPhase("playing");
  };

  const getRating = () => {
    if (saved===5) return { emoji:"🏆", title:"Legendary Keeper!", msg:"Perfect 5/5 saves — nothing gets past you!" };
    if (saved===4) return { emoji:"⭐", title:"World Class!",      msg:"4/5 saves — incredible reflexes!" };
    if (saved===3) return { emoji:"👍", title:"Solid Shift!",       msg:"3/5 saves — a strong outing between the sticks!" };
    if (saved===2) return { emoji:"😅", title:"Tough Game!",        msg:"2/5 saves — they had your number today!" };
    if (saved===1) return { emoji:"😬", title:"Rocky Game!",        msg:"1/5 saves — time to work on your reactions!" };
    return            { emoji:"🙈", title:"Nightmare Shift!",     msg:"0/5 saves — back to training for you!" };
  };

  const s = { fontFamily:"'Inter','Segoe UI',sans-serif" };

  // ── INTRO ──
  if (phase==="intro") return (
    <div style={{ ...s, minHeight:"100vh", background:"#0d1117", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <link href="https://fonts.googleapis.com/css2?family=Oswald:wght@400;600;700&family=Inter:wght@400;500;600&display=swap" rel="stylesheet" />
      <style>{`@keyframes bounce{from{transform:translateY(0)}to{transform:translateY(-14px)}} @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}`}</style>
      <div style={{ textAlign:"center", maxWidth:400 }}>
        <div style={{ fontSize:80, marginBottom:16, display:"inline-block", animation:"bounce 0.8s infinite alternate" }}>🧤</div>
        <div style={{ color:"#3b82f6", fontFamily:"'Oswald',sans-serif", fontSize:11, letterSpacing:4, marginBottom:8 }}>GRASSROOTING</div>
        <h1 style={{ color:"#f9fafb", fontFamily:"'Oswald',sans-serif", fontSize:38, fontWeight:700, marginBottom:8, lineHeight:1.1 }}>SAVE THE<br/>PENALTY</h1>
        <p style={{ color:"#6b7280", fontSize:13, lineHeight:1.7, marginBottom:24 }}>5 penalties. Watch the striker.<br/>React fast and pick your dive — you've got about a second!</p>
        <button onClick={()=>setPhase("playing")} style={{ background:"#3b82f6", color:"#fff", border:"none", borderRadius:12, padding:"14px 44px", fontFamily:"'Oswald',sans-serif", fontWeight:700, fontSize:20, cursor:"pointer", letterSpacing:1, boxShadow:"0 4px 28px #3b82f644" }}>
          IN GOAL 🧤
        </button>
        {onBack && <div style={{ marginTop:20 }}>
          <button onClick={onBack} style={{ background:"none", border:"none", color:"#6b7280", fontSize:12, cursor:"pointer", textDecoration:"underline" }}>← Back to Penalty Shootout</button>
        </div>}
      </div>
    </div>
  );

  // ── RESULT ──
  if (phase==="result") {
    const r = getRating();
    return (
      <div style={{ ...s, minHeight:"100vh", background:"#0d1117", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
        <link href="https://fonts.googleapis.com/css2?family=Oswald:wght@400;600;700&family=Inter:wght@400;500;600&display=swap" rel="stylesheet" />
        <div style={{ textAlign:"center", maxWidth:400, width:"100%" }}>
          <div style={{ fontSize:72, marginBottom:12 }}>{r.emoji}</div>
          <div style={{ color:"#3b82f6", fontFamily:"'Oswald',sans-serif", fontSize:11, letterSpacing:4, marginBottom:8 }}>SAVES MADE</div>
          <div style={{ color:"#f9fafb", fontFamily:"'Oswald',sans-serif", fontSize:68, fontWeight:700, lineHeight:1, marginBottom:4 }}>
            {saved}<span style={{ color:"#374151", fontSize:36 }}>/5</span>
          </div>
          <div style={{ color:"#3b82f6", fontFamily:"'Oswald',sans-serif", fontSize:24, fontWeight:700, marginBottom:8 }}>{r.title}</div>
          <div style={{ color:"#9ca3af", fontSize:14, marginBottom:28, lineHeight:1.6 }}>{r.msg}</div>
          <div style={{ display:"flex", gap:10, justifyContent:"center", marginBottom:28 }}>
            {history.map((h,i) => (
              <div key={i} style={{ width:36, height:36, borderRadius:"50%", background:h==="saved"?"#10b98133":"#ef444422", border:`2px solid ${h==="saved"?"#10b981":"#ef4444"}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>
                {h==="saved"?"🧤":"⚽"}
              </div>
            ))}
          </div>
          <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap", marginBottom:28 }}>
            <button onClick={restart} style={{ background:"#3b82f6", color:"#fff", border:"none", borderRadius:10, padding:"12px 28px", fontFamily:"'Oswald',sans-serif", fontWeight:700, fontSize:15, cursor:"pointer", letterSpacing:1 }}>SAVE AGAIN 🔄</button>
            <button onClick={()=>setPhase("intro")} style={{ background:"#1f2937", color:"#9ca3af", border:"1px solid #374151", borderRadius:10, padding:"12px 28px", fontFamily:"'Oswald',sans-serif", fontWeight:700, fontSize:15, cursor:"pointer" }}>HOME</button>
          </div>
          <div style={{ background:"#111827", border:"1px solid #1f2937", borderRadius:12, padding:"16px 20px" }}>
            <div style={{ color:"#3b82f6", fontFamily:"'Oswald',sans-serif", fontSize:12, letterSpacing:2, marginBottom:6 }}>PLAYED ON GRASSROOTING</div>
            <div style={{ color:"#6b7280", fontSize:12, lineHeight:1.6 }}>Find grassroots football teams near you — U7 to U16, every league, everywhere.</div>
            <div style={{ color:"#10b981", fontSize:12, fontWeight:700, marginTop:6 }}>grassrooting.co.uk ⚽</div>
          </div>
        </div>
      </div>
    );
  }

  // ── GAME ──
  return (
    <div style={{ ...s, minHeight:"100vh", background:"#0d1117", display:"flex", flexDirection:"column", alignItems:"center", padding:"20px 16px", gap:16 }}>
      <link href="https://fonts.googleapis.com/css2?family=Oswald:wght@400;600;700&family=Inter:wght@400;500;600&display=swap" rel="stylesheet" />
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>

      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", width:"100%", maxWidth:400 }}>
        <div>
          <span style={{ color:"#f9fafb", fontFamily:"'Oswald',sans-serif", fontSize:18, fontWeight:700, letterSpacing:1 }}>GRASS</span>
          <span style={{ color:"#10b981", fontFamily:"'Oswald',sans-serif", fontSize:18, fontWeight:700, letterSpacing:1 }}>ROOTING</span>
        </div>
        <div style={{ background:"#111827", border:"1px solid #1f2937", borderRadius:20, padding:"4px 16px" }}>
          <span style={{ color:"#3b82f6", fontFamily:"'Oswald',sans-serif", fontWeight:700, fontSize:18 }}>{saved}</span>
          <span style={{ color:"#4b5563", fontFamily:"'Oswald',sans-serif", fontSize:14 }}> / {TOTAL}</span>
        </div>
      </div>

      <ScoreRow saved={saved} total={TOTAL} taken={taken} />

      <div style={{ position:"relative", width:"100%", maxWidth:400, height:280, background:"linear-gradient(180deg,#14532d 0%,#166534 40%,#15803d 100%)", borderRadius:16, overflow:"hidden", border:"2px solid #166534", boxShadow:"0 8px 32px #000a" }}>
        <div style={{ position:"absolute", bottom:0, left:"18%", right:"18%", height:"36%", border:"2px solid #ffffff20", borderBottom:"none", borderRadius:"4px 4px 0 0" }} />
        <div style={{ position:"absolute", bottom:0, left:"34%", right:"34%", height:"18%", border:"2px solid #ffffff20", borderBottom:"none" }} />
        <div style={{ position:"absolute", bottom:"18%", left:"50%", transform:"translateX(-50%)", width:56, height:56, borderRadius:"50%", border:"2px solid #ffffff20" }} />
        <div style={{ position:"absolute", bottom:"18%", left:"50%", transform:"translateX(-50%) translateX(-3px)", width:6, height:6, borderRadius:"50%", background:"#ffffff33" }} />

        <Goal />
        <PlayerKeeper diving={diving} diveZone={diveZone} />
        <Striker shooting={shooting} shotZone={shotZone} />
        <Ball shooting={shooting} targetZone={shotZone} />

        {result && (
          <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", background:result==="saved"?"#10b98118":"#ef444418", zIndex:10, borderRadius:14 }}>
            <div style={{ fontSize:60 }}>{result==="saved"?"🧤":"😬"}</div>
          </div>
        )}
      </div>

      <div style={{ minHeight:32, textAlign:"center", maxWidth:340 }}>
        {comment
          ? <div style={{ color:comment.includes("SAVE")||comment.includes("DENIED")||comment.includes("read")||comment.includes("reflexes")||comment.includes("Stonewall")?"#10b981":"#ef4444", fontFamily:"'Oswald',sans-serif", fontSize:15, fontWeight:700, letterSpacing:0.5 }}>{comment}</div>
          : <div style={{ color: shooting ? "#f59e0b" : "#4b5563", fontSize: shooting ? 18 : 13, fontWeight: shooting ? 700 : 400, fontFamily: shooting ? "'Oswald',sans-serif" : "inherit" }}>
              {taken < TOTAL
                ? (shooting
                    ? (countdown > 0
                        ? `PICK YOUR DIVE! ${countdown}...`
                        : "SHOOTING...")
                    : `Save ${taken+1} of ${TOTAL} — ready...`)
                : ""}
            </div>
        }
      </div>

      {taken < TOTAL && !shooting && (
        <button onClick={startShot} style={{ background:"#ef4444", color:"#fff", border:"none", borderRadius:12, padding:"14px 36px", fontFamily:"'Oswald',sans-serif", fontWeight:700, fontSize:16, cursor:"pointer", letterSpacing:1, boxShadow:"0 4px 20px #ef444444" }}>
          FACE THE SHOT ⚽
        </button>
      )}

      {taken < TOTAL && shooting && (
        <ZoneButtons onDive={choose} disabled={!canChoose} selected={selectedZone} />
      )}
    </div>
  );
}
